import { useEffect, useRef, useState, type ReactNode } from 'react';
import { CHAT_FILE_HINT } from '@jerome2/shared';
import { Icon } from '../ui/Icon.tsx';
import { dragHasFiles, filesFrom, pastedFiles } from './files.ts';

// Het hele werkblad van de chat is een sleepvlak: zolang er een bestand boven hangt, staat er een rand met
// "Laat los om toe te voegen" en de grens; loslaten zet de bestanden bij het bericht in het vak. Plakken (Cmd-V of Ctrl-V)
// van een schermafbeelding kan ook buiten het vak: na een klik in dit werkblad (met twee panelen naast elkaar telt alleen het
// werkblad waar het laatst is geklikt), en niet in een ander invoerveld (de mail-editor plakt zelf). Alleen sleepacties
// met bestanden tellen: tekst of een link slepen gaat zoals altijd, en geplakte tekst uit Excel of Word blijft tekst.
// De gesprekkenkolom van de schil is ook zo'n vlak, met een eigen testhaak en zin ("Laat los voor een nieuw gesprek");
// ingeklapt tot de strook van 44 px (`compact`) staat in de rand alleen het icoon, met de zin voor een schermlezer.

const editable = (el: Element | null): boolean => Boolean(el && (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement || (el as HTMLElement).isContentEditable));

export function DropZone(props: { onFiles: (files: File[]) => void; children: ReactNode; className?: string | undefined; testId?: string; label?: string; compact?: boolean }) {
  const label = props.label ?? 'Laat los om toe te voegen';
  const [over, setOver] = useState(false);
  const depth = useRef(0);
  const ref = useRef<HTMLDivElement>(null);
  const onFiles = useRef(props.onFiles);
  onFiles.current = props.onFiles;
  /** Of de laatste klik in dit werkblad was: dan hoort een plakactie zonder focus (op de pagina zelf) hierbij. */
  const lastInside = useRef(false);

  useEffect(() => {
    const onPointer = (e: PointerEvent) => {
      lastInside.current = Boolean(ref.current && e.target instanceof Node && ref.current.contains(e.target));
    };
    const onPaste = (e: ClipboardEvent) => {
      // Het vak zelf (of een ander veld) heeft het al afgehandeld.
      if (e.defaultPrevented) return;
      const root = ref.current;
      if (!root) return;
      const target = e.target instanceof Element ? e.target : null;
      const inside = Boolean(target && root.contains(target));
      if (!inside && !(lastInside.current && (!target || target === document.body || target === document.documentElement))) return;
      if (editable(target)) return;
      const files = pastedFiles(e.clipboardData);
      if (!files.length) return;
      e.preventDefault();
      onFiles.current(files);
    };
    // Losgelaten buiten het venster of afgebroken: dan verdwijnt de rand ook.
    const reset = () => {
      depth.current = 0;
      setOver(false);
    };
    document.addEventListener('pointerdown', onPointer, true);
    document.addEventListener('paste', onPaste);
    window.addEventListener('dragend', reset);
    window.addEventListener('drop', reset);
    return () => {
      document.removeEventListener('pointerdown', onPointer, true);
      document.removeEventListener('paste', onPaste);
      window.removeEventListener('dragend', reset);
      window.removeEventListener('drop', reset);
    };
  }, []);

  return (
    <div
      ref={ref}
      data-testid={props.testId ?? 'chat-werkblad'}
      className={`relative ${props.className ?? ''}`}
      onDragEnter={(e) => {
        if (!dragHasFiles(e.dataTransfer)) return;
        e.preventDefault();
        depth.current += 1;
        setOver(true);
      }}
      onDragOver={(e) => {
        if (!dragHasFiles(e.dataTransfer)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
      }}
      onDragLeave={(e) => {
        if (!dragHasFiles(e.dataTransfer)) return;
        depth.current = Math.max(0, depth.current - 1);
        if (depth.current === 0) setOver(false);
      }}
      onDrop={(e) => {
        if (!dragHasFiles(e.dataTransfer)) return;
        e.preventDefault();
        depth.current = 0;
        setOver(false);
        const files = filesFrom(e.dataTransfer);
        if (files.length) onFiles.current(files);
      }}
    >
      {props.children}
      {over ? (
        <div data-testid="sleepvlak" role="status" className={`pointer-events-none absolute z-40 rounded-2xl border-[3px] border-dashed border-accent bg-tint/85 flex flex-col items-center justify-center gap-2 text-center text-accent ${props.compact ? 'inset-1' : 'inset-2 px-6'}`}>
          <Icon name="clip" size={props.compact ? 18 : 30} />
          {props.compact ? (
            <span className="sr-only">{label}</span>
          ) : (
            <>
              <div className="font-display text-[20px] font-semibold">{label}</div>
              <div className="text-[13px] text-ink-soft">{CHAT_FILE_HINT}</div>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
